/**
 * Curated scheme → official "Apply" URL map.
 *
 * Why static?  The LLM sometimes hallucinates URLs or gives a useful portal name
 * only ("PM-KISAN portal").  We want every scheme card in the KMS Smart results
 * to carry a *working* Apply button, so we maintain a vetted list of government
 * portals here and match by normalised keywords from the scheme name.
 *
 * Add new schemes as they appear in analyses — each entry needs:
 *   - `match`   : lowercase keyword OR array of alternatives, must be unique
 *                 enough that no unrelated scheme matches
 *   - `url`     : canonical official application / information URL (https)
 *   - `label`   : short CTA label (default: "Apply")  — optional
 */

const APPLY_LINKS = [
  // ── Central flagship ──────────────────────────────────────────────────
  { match: ['pm-kisan', 'pm kisan', 'pradhan mantri kisan samman nidhi'], url: 'https://pmkisan.gov.in/' },
  { match: ['pmfby', 'pradhan mantri fasal bima', 'fasal bima yojana'],    url: 'https://pmfby.gov.in/' },
  { match: ['pmmsy', 'matsya sampada'],                                    url: 'https://pmmsy.dof.gov.in/' },
  { match: ['pmay-g', 'pradhan mantri awas yojana gramin', 'awaas gramin'],url: 'https://pmayg.nic.in/' },
  { match: ['pmay-u', 'pradhan mantri awas yojana urban', 'awas urban'],   url: 'https://pmay-urban.gov.in/' },
  { match: ['pmay', 'pradhan mantri awas yojana', 'awas yojana'],          url: 'https://pmaymis.gov.in/' },
  { match: ['pmjay', 'ayushman bharat', 'ayushman'],                       url: 'https://beneficiary.nha.gov.in/' },
  { match: ['sukanya samriddhi', 'ssy'],                                   url: 'https://www.nsiindia.gov.in/InternalPage.aspx?Id_Pk=89' },
  { match: ['pmuy', 'pradhan mantri ujjwala', 'ujjwala yojana'],           url: 'https://www.pmuy.gov.in/' },
  { match: ['pmvy', 'pm vishwakarma', 'vishwakarma yojana'],               url: 'https://pmvishwakarma.gov.in/' },
  { match: ['standup india', 'stand-up india', 'stand up india'],          url: 'https://www.standupmitra.in/' },
  { match: ['pmmy', 'mudra yojana', 'pradhan mantri mudra'],               url: 'https://www.mudra.org.in/' },
  { match: ['pmsby', 'suraksha bima'],                                     url: 'https://www.jansuraksha.gov.in/' },
  { match: ['pmjjby', 'jeevan jyoti bima'],                                url: 'https://www.jansuraksha.gov.in/' },
  { match: ['atal pension yojana', 'apy'],                                 url: 'https://enps.nsdl.com/eNPS/NationalPensionSystem.html' },
  { match: ['pmkvy', 'pradhan mantri kaushal vikas', 'kaushal vikas'],     url: 'https://www.pmkvyofficial.org/' },
  { match: ['pm svanidhi', 'svanidhi'],                                    url: 'https://pmsvanidhi.mohua.gov.in/' },
  { match: ['kcc', 'kisan credit card'],                                   url: 'https://pmkisan.gov.in/KCC.aspx' },
  { match: ['rkvy', 'rashtriya krishi vikas'],                             url: 'https://rkvy.nic.in/' },
  { match: ['pmjdy', 'jan dhan yojana'],                                   url: 'https://pmjdy.gov.in/' },
  { match: ['pmmvy', 'matru vandana'],                                     url: 'https://wcd.nic.in/schemes/pradhan-mantri-matru-vandana-yojana' },
  { match: ['post-matric scholarship', 'nsp', 'national scholarship'],     url: 'https://scholarships.gov.in/' },
  { match: ['post matric scholarship'],                                    url: 'https://scholarships.gov.in/' },
  { match: ['pradhan mantri jan arogya'],                                  url: 'https://beneficiary.nha.gov.in/' },
  { match: ['e-shram', 'eshram', 'shram card'],                            url: 'https://eshram.gov.in/' },
  { match: ['startup india'],                                              url: 'https://www.startupindia.gov.in/' },

  // ── Andhra Pradesh ───────────────────────────────────────────────────
  { match: ['ysr rythu bharosa', 'rythu bharosa'],                         url: 'https://ysrrythubharosa.ap.gov.in/' },
  { match: ['ap rythu bandhu', 'andhra pradesh rythu bandhu'],             url: 'https://ysrrythubharosa.ap.gov.in/' },
  { match: ['jagananna vidya deevena'],                                    url: 'https://jnanabhumi.ap.gov.in/' },
  { match: ['jagananna vasathi deevena'],                                  url: 'https://jaganannavasathi.ap.gov.in/' },
  { match: ['ap drone subsidy', 'drone subsidy'],                          url: 'https://apagrisnet.gov.in/' },
  { match: ['ysr cheyutha', 'cheyutha'],                                   url: 'https://navasakam.ap.gov.in/' },
  { match: ['ysr pension kanuka'],                                         url: 'https://sspensions.ap.gov.in/' },

  // ── Telangana ────────────────────────────────────────────────────────
  { match: ['rythu bandhu', 'telangana rythu bandhu'],                     url: 'https://rythubandhu.telangana.gov.in/' },
  { match: ['kcr kit', 'telangana kcr kit'],                               url: 'https://kcrkit.telangana.gov.in/' },
  { match: ['kalyana lakshmi', 'shaadi mubarak'],                          url: 'https://telanganaepass.cgg.gov.in/' },
  { match: ['asara pension'],                                              url: 'https://aasara.telangana.gov.in/' },

  // ── Karnataka ────────────────────────────────────────────────────────
  { match: ['gruha lakshmi'],                                              url: 'https://sevasindhugs.karnataka.gov.in/' },
  { match: ['gruha jyothi'],                                               url: 'https://sevasindhugs.karnataka.gov.in/' },
  { match: ['anna bhagya'],                                                url: 'https://ahara.kar.nic.in/' },
  { match: ['bhagyalakshmi'],                                              url: 'https://sevasindhuservices.karnataka.gov.in/' },
  { match: ['shakti scheme', 'karnataka shakti'],                          url: 'https://sevasindhugs.karnataka.gov.in/' },
];

const DEFAULT_LABEL = 'Apply';

/**
 * Resolve the best-known Apply URL for a given scheme.
 *
 * Priority:
 *   1. Prefer the URL the LLM supplied (if it looks http(s))
 *   2. Fall back to a curated match from `APPLY_LINKS`
 *   3. Return `null` so the caller can omit the button entirely
 */
export function getSchemeApplyLink(scheme) {
  if (!scheme) return null;

  // 1. Trust an LLM-provided URL only when it already looks canonical.
  //    We don't want to send users to hallucinated `.gov.in/fake-page` links.
  const llmUrl = scheme.application_url || scheme.apply_url || scheme.url;
  if (typeof llmUrl === 'string' && /^https?:\/\//i.test(llmUrl)) {
    try {
      const host = new URL(llmUrl).hostname.toLowerCase();
      if (host.endsWith('.gov.in') || host.endsWith('.nic.in') || host.endsWith('.org.in')) {
        return { url: llmUrl, label: DEFAULT_LABEL, source: 'llm' };
      }
    } catch {
      /* malformed URL — fall through to static map */
    }
  }

  // 2. Static keyword match against the scheme name.
  const haystack = `${scheme.scheme_name || scheme.name || ''}`.toLowerCase();
  if (!haystack.trim()) return null;

  for (const entry of APPLY_LINKS) {
    const keywords = Array.isArray(entry.match) ? entry.match : [entry.match];
    if (keywords.some((k) => haystack.includes(k.toLowerCase()))) {
      return { url: entry.url, label: entry.label || DEFAULT_LABEL, source: 'curated' };
    }
  }

  return null;
}
